import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  isPremium: boolean;
  isBlocked: boolean;
  isDisabled: boolean;
  streak: number;
  eloRating: number;
  createdAt: string;
  totalRuns: number;
  officialWins: number;
  lastPlayedAt: string | null;
}

export function AdminUsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [disabledUsers, setDisabledUsers] = useState<AdminUser[]>([]);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);

  const load = () => {
    api<{ users: AdminUser[]; disabledUsers?: AdminUser[] }>('/admin/users')
      .then((res) => {
        setUsers(res.users);
        setDisabledUsers(res.disabledUsers || []);
      })
      .catch(() => setError(t('common.error')));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = showDisabled ? disabledUsers : users;
    if (!q) return list;
    return list.filter(
      (u) => u.email?.toLowerCase().includes(q)
        || u.displayName?.toLowerCase().includes(q),
    );
  }, [users, disabledUsers, filter, showDisabled]);

  const runAction = async (
    userId: string,
    action: () => Promise<unknown>,
  ) => {
    setBusyId(userId);
    setError('');
    try {
      await action();
      load();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  const togglePremium = (user: AdminUser) => runAction(user.id, () =>
    api('/admin/premium', {
      method: 'POST',
      body: JSON.stringify({ userId: user.id, premium: !user.isPremium }),
    }));

  const toggleBlock = (user: AdminUser) => runAction(user.id, () =>
    api('/admin/block', {
      method: 'POST',
      body: JSON.stringify({ userId: user.id, blocked: !user.isBlocked }),
    }));

  const disableUser = (user: AdminUser) => {
    if (!window.confirm(t('admin.users.deleteConfirm'))) return;
    runAction(user.id, () =>
      api('/admin/disable', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id, disabled: true }),
      }));
  };

  const restoreUser = (user: AdminUser) => runAction(user.id, () =>
    api('/admin/disable', {
      method: 'POST',
      body: JSON.stringify({ userId: user.id, disabled: false }),
    }));

  if (error && !users.length && !disabledUsers.length) {
    return <p className="admin-msg">{error}</p>;
  }
  if (!users.length && !disabledUsers.length && !error) {
    return <p className="admin-msg">{t('common.loading')}</p>;
  }

  return (
    <div className="admin-users">
      <div className="admin-filters">
        <input
          type="search"
          className="admin-search"
          placeholder={t('admin.users.search')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button
          type="button"
          className={`btn secondary admin-btn-sm${showDisabled ? ' active' : ''}`}
          onClick={() => setShowDisabled((v) => !v)}
        >
          {showDisabled ? t('admin.users.showActive') : t('admin.users.showDeleted')}
          {!showDisabled && disabledUsers.length > 0 && ` (${disabledUsers.length})`}
        </button>
      </div>

      {error && <p className="admin-msg admin-msg-error">{error}</p>}

      {filtered.length === 0 ? (
        <p className="admin-msg">{t('admin.users.empty')}</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.users.email')}</th>
              <th>{t('admin.users.name')}</th>
              <th>{t('admin.users.status')}</th>
              <th>{t('admin.users.runs')}</th>
              <th>{t('admin.users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => {
              const busy = busyId === user.id;
              const modifiable = !user.isAdmin;

              return (
                <tr
                  key={user.id}
                  className={[
                    user.isBlocked ? 'admin-row-blocked' : '',
                    user.isDisabled ? 'admin-row-disabled' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td>{user.email}</td>
                  <td>
                    {user.displayName}
                    {user.isAdmin && (
                      <span className="admin-badge">{t('admin.users.admin')}</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-user-badges">
                      {user.isPremium && (
                        <span className="admin-badge admin-badge-premium">{t('admin.users.premium')}</span>
                      )}
                      {user.isBlocked && (
                        <span className="admin-badge admin-badge-danger">{t('admin.users.blocked')}</span>
                      )}
                      {user.isDisabled && (
                        <span className="admin-badge admin-badge-muted">{t('admin.users.deleted')}</span>
                      )}
                      {!user.isPremium && !user.isBlocked && !user.isDisabled && (
                        <span className="admin-muted-dash">—</span>
                      )}
                    </div>
                  </td>
                  <td>{user.totalRuns}</td>
                  <td>
                    {modifiable ? (
                      <div className="admin-user-actions">
                        <button
                          type="button"
                          className="btn secondary admin-btn-sm"
                          disabled={busy}
                          onClick={() => togglePremium(user)}
                        >
                          {user.isPremium
                            ? t('admin.users.revokePremium')
                            : t('admin.users.grantPremium')}
                        </button>
                        {!user.isDisabled && (
                          <button
                            type="button"
                            className={`btn secondary admin-btn-sm${user.isBlocked ? ' admin-btn-warn' : ''}`}
                            disabled={busy}
                            onClick={() => toggleBlock(user)}
                          >
                            {user.isBlocked
                              ? t('admin.users.unblock')
                              : t('admin.users.block')}
                          </button>
                        )}
                        {user.isDisabled ? (
                          <button
                            type="button"
                            className="btn secondary admin-btn-sm"
                            disabled={busy}
                            onClick={() => restoreUser(user)}
                          >
                            {t('admin.users.restore')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn ghost admin-btn-sm admin-btn-danger"
                            disabled={busy}
                            onClick={() => disableUser(user)}
                          >
                            {t('admin.users.delete')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="admin-muted-dash">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
